import React, { useState, useEffect } from "react";
import { Card, Input, Button, Tag, Space, Tooltip, Table, Select } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableClubRow from "./SortableClubRow";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ClubManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubToDelete, setClubToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  // State for form errors
  const [formErrors, setFormErrors] = useState({});

  // Pagination, search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedStatusFilter = useDebounce(statusFilter, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [clubList, setClubList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // Status filter options
  const STATUS_OPTIONS = [
    { value: "active", label: t("common.active") },
    { value: "inactive", label: t("common.inactive") },
  ];

  // API hooks
  const { execute: fetchList, loading: loadingList } = useGet(
    `${API_ENDPOINTS.CLUBS_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&status=${statusFilter || ""}`,
  );
  const { post, loading: creating } = usePost(API_ENDPOINTS.CLUBS_CREATE);
  const { execute: getById, loading: loadingById } = useDynamicApi("GET");
  const { execute: putData, loading: updating } = useDynamicApi("PUT");
  const { execute: patchData, loading: updatingStatus } =
    useDynamicApi("PATCH");
  const { execute: deleteData, loading: deleting } = useDynamicApi("DELETE");
  const { execute: postData, loading: posting } = useDynamicApi("POST");

  // Function to get token
  const getToken = () => {
    return (
      localStorage.getItem("adminToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Loading state
  const loading =
    loadingList ||
    loadingById ||
    updating ||
    updatingStatus ||
    deleting ||
    isReordering ||
    posting ||
    submitting;

  // Load data with current pagination, search and filter
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        // Sort by display_order when no search term and no filter
        let data = response.data || [];
        if (!searchTerm && !statusFilter) {
          data = data.sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0),
          );
        }
        setClubList(data);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const reorderClubs = async (orderedIds) => {
    if (!orderedIds || orderedIds.length === 0) {
      Toast.error(t("attackManager.noItemsToReorder"));
      return false;
    }
    try {
      setIsReordering(true);
      const response = await postData(API_ENDPOINTS.CLUBS_REORDER, {
        ordered_ids: orderedIds,
      });
      if (response?.status === true) {
        Toast.success(response?.message || t("attackManager.reorderedSuccess"));
        return true;
      }
      Toast.error(response?.message || t("attackManager.failedReorder"));
      return false;
    } catch (error) {
      Toast.error(error.message || t("attackManager.failedReorder"));
      return false;
    } finally {
      setIsReordering(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);
    if (searchTerm || statusFilter) return;
    if (!over || active.id === over.id) return;
    const oldIndex = clubList.findIndex((c) => c.id === active.id);
    const newIndex = clubList.findIndex((c) => c.id === over.id);
    if (oldIndex !== newIndex) {
      const newOrderedList = arrayMove(clubList, oldIndex, newIndex);
      setClubList(newOrderedList);
      const success = await reorderClubs(newOrderedList.map((c) => c.id));
      if (!success) setClubList(clubList);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setIsDragging(false);
  };

  // Initial data fetch
  // useEffect(() => {
  //   loadData();
  // }, [currentPage, pageSize]);

  // Handle search and filter - triggers when debounced values change
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm, statusFilter, currentPage, pageSize]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form function - Only sets form errors, no toast
  const validateForm = () => {
    const errors = {};

    // Validate name
    if (!formData.name.trim()) {
      errors.name = t("clubManager.nameRequired");
    } else if (formData.name.trim().length < 2) {
      errors.name = t("clubManager.nameMinLength");
    } else if (formData.name.trim().length > 100) {
      errors.name = t("clubManager.nameMaxLength");
    }

    // Validate description
    if (!formData.description.trim()) {
      errors.description = t("common.description") + " " + t("common.required");
    } else if (formData.description.trim().length < 5) {
      errors.description =
        t("clubManager.descriptionMinLength") ||
        "Description must be at least 5 characters";
    } else if (formData.description.trim().length > 500) {
      errors.description =
        t("clubManager.descriptionMaxLength") ||
        "Description must not exceed 500 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
    });
    setFormErrors({});
    setSelectedClub(null);
    setIsEditMode(false);
    setIsViewMode(false);
  };

  // Handle API errors
  const handleApiError = (error) => {
    if (error?.response?.data?.errors) {
      setFormErrors(error.response.data.errors);
    } else if (error?.response?.data?.message) {
      Toast.error(error.response.data.message);
    } else {
      Toast.error(error?.message || t("common.error"));
    }
  };

  // Handle create club
  const handleCreate = async (e) => {
    e?.preventDefault?.();

    // Only validate, no toast for validation errors
    if (!validateForm()) {
      return; // Errors show below fields, no toast
    }

    setSubmitting(true);

    try {
      const response = await post(formData);

      if (response?.status === true) {
        Toast.success(response?.message || t("clubManager.createdSuccess"));
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("clubManager.failedCreate"));
        }
      }
    } catch (error) {
      console.error("Error creating club:", error);
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle update club
  const handleUpdate = async (e) => {
    e?.preventDefault?.();

    // Only validate, no toast for validation errors
    if (!validateForm()) {
      return; // Errors show below fields, no toast
    }

    setSubmitting(true);

    try {
      const endpoint = `${API_ENDPOINTS.CLUBS_UPDATE}/${selectedClub.id}`;
      const response = await putData(endpoint, formData);

      if (response?.status === true) {
        Toast.success(response?.message || t("clubManager.updatedSuccess"));
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("clubManager.failedUpdate"));
        }
      }
    } catch (error) {
      console.error("Error updating club:", error);
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (club) => {
    try {
      const newStatus = !club.is_active;
      const endpoint = `${API_ENDPOINTS.CLUBS_UPDATE}/${club.id}`;

      const response = await patchData(endpoint, {
        is_active: newStatus,
      });

      if (response?.status === true) {
        Toast.success(
          response?.message ||
            (newStatus ? t("common.active") : t("common.inactive")),
        );
        await loadData();
      } else {
        Toast.error(response?.message || t("attackManager.failedUpdateStatus"));
      }
    } catch (error) {
      console.error("Error updating club status:", error);
      Toast.error(error.message || t("attackManager.failedUpdateStatus"));
    }
  };

  const openDeleteModal = (club) => {
    setClubToDelete(club);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clubToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.CLUBS_UPDATE}/${clubToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(response?.message || t("clubManager.deletedSuccess"));
        await loadData();
      } else {
        Toast.error(response?.message || t("clubManager.failedDelete"));
      }
    } catch (error) {
      console.error("Error deleting club:", error);
      Toast.error(error.message || t("clubManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setClubToDelete(null);
    }
  };

  // Open modal for create
  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  // Helper function to fetch club by ID
  const fetchClubById = async (id) => {
    const endpoint = `${API_ENDPOINTS.CLUBS_UPDATE}/${id}`;
    const response = await getById(endpoint);
    return response;
  };

  // Open modal for edit
  const openEditModal = async (club) => {
    try {
      const response = await fetchClubById(club.id);

      if (response?.status === true && response.data) {
        setSelectedClub(response.data);
        setFormData({
          name: response.data.name,
          description: response.data.description,
        });
        setFormErrors({});
        setIsEditMode(true);
        setIsViewMode(false);
        setIsModalOpen(true);
      } else {
        Toast.error(t("clubManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching club details:", error);
      Toast.error(error.message || t("clubManager.failedFetchDetails"));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isDragEnabled = !searchTerm && !statusFilter && !loading;
  const activeClub = activeId ? clubList.find((c) => c.id === activeId) : null;

  return (
    <>
      <Card
        title={t("clubManager.title")}
        extra={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {(searchTerm || statusFilter) && (
              <span
                style={{
                  fontSize: 13,
                  color: "#856404",
                  background: "#fff3cd",
                  padding: "6px 12px",
                  borderRadius: 4,
                }}
              >
                {t("common.clearSearchToReorder")}
              </span>
            )}
            <Button type="primary" onClick={openCreateModal} disabled={loading}>
              {t("clubManager.createClub")}
            </Button>
          </span>
        }
      >
        <div style={{ display: "flex", gap: "16px", marginBottom: 16 }}>
          <Input
            placeholder={t("clubManager.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ maxWidth: 400 }}
            disabled={loading}
          />
          <Select
            placeholder={t("common.filterByStatus")}
            value={statusFilter || undefined}
            onChange={(value) => setStatusFilter(value || "")}
            options={STATUS_OPTIONS}
            style={{ width: 200 }}
            allowClear
            disabled={loading}
          />
        </div>

        <div
          style={{
            position: "relative",
            background: "#fff",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {loadingList && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <span
                style={{
                  border: "3px solid #f0f0f0",
                  borderTopColor: "#066744",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  animation: "spin 0.8s linear infinite",
                }}
              />
            </div>
          )}
          {isReordering && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9,
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  background: "#fff",
                  padding: "8px 16px",
                  borderRadius: 20,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {t("common.savingOrder")}
              </span>
            </div>
          )}

          <div className="table-scroll-wrap">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <table
                className="data-table"
                style={{ width: "100%", borderCollapse: "collapse" }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#fafafa",
                      borderBottom: "2px solid #eee",
                    }}
                  >
                    <th
                      style={{
                        minWidth: 40,
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    />
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("clubManager.clubName")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("common.description")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("clubManager.createdBy") || "Created By"}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("clubManager.memberCount") || "Member Count"}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("common.createdAt")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("common.lastUpdated")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("common.status")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!loadingList && clubList.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          padding: 40,
                          textAlign: "center",
                          color: "#666",
                        }}
                      >
                        {searchTerm || statusFilter
                          ? t("clubManager.noMatchingClubs")
                          : t("clubManager.noClubsFound")}
                      </td>
                    </tr>
                  ) : (
                    <SortableContext
                      items={clubList.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                      disabled={!isDragEnabled}
                    >
                      {clubList.map((club) => (
                        <SortableClubRow
                          key={club.id}
                          club={club}
                          loading={loading}
                          updatingStatus={updatingStatus}
                          searchTerm={searchTerm}
                          formatDate={formatDate}
                          handleToggleActive={handleToggleActive}
                          openEditModal={openEditModal}
                          openDeleteModal={openDeleteModal}
                          isDragging={isDragging && activeId === club.id}
                          isDragEnabled={isDragEnabled}
                        />
                      ))}
                    </SortableContext>
                  )}
                </tbody>
              </table>

              <DragOverlay>
                {activeClub && (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      background: "#fff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      borderRadius: 8,
                    }}
                  >
                    <tbody>
                      <SortableClubRow
                        club={activeClub}
                        loading={loading}
                        updatingStatus={updatingStatus}
                        searchTerm={searchTerm}
                        formatDate={formatDate}
                        handleToggleActive={handleToggleActive}
                        openEditModal={openEditModal}
                        openDeleteModal={openDeleteModal}
                        isDragging
                        isOverlay
                        isDragEnabled={isDragEnabled}
                      />
                    </tbody>
                  </table>
                )}
              </DragOverlay>
            </DndContext>
          </div>

          {!loadingList && clubList.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                padding: "16px 0",
                borderTop: "1px solid #eee",
              }}
            >
              <span style={{ fontSize: 14, color: "#666" }}>
                {t("common.showing")} {(currentPage - 1) * pageSize + 1}{" "}
                {t("common.to")} {Math.min(currentPage * pageSize, totalCount)}{" "}
                {t("common.of")} {totalCount}{" "}
                {t("clubManager.clubs") || "clubs"}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  {t("common.previous")}
                </Button>
                <Button type="primary" disabled>
                  {currentPage}
                </Button>
                <Button
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  {t("common.next")}
                </Button>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                  }}
                >
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} {t("common.perPage")}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isEditMode
              ? t("clubManager.editClub")
              : t("clubManager.createNewClub")
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? submitting
                ? t("clubManager.savingChanges")
                : t("common.update")
              : submitting
                ? t("clubManager.creating")
                : t("common.create")
          }
          showConfirm={true}
          confirmDisabled={submitting}
        >
          <form
            className="modal-form"
            onSubmit={isEditMode ? handleUpdate : handleCreate}
          >
            <div className="form-group">
              <label htmlFor="name">
                {t("clubManager.clubName")} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t("clubManager.enterClubName")}
                required
                disabled={submitting}
                className={formErrors.name ? "input-error" : ""}
                maxLength={100}
              />
              {formErrors.name && (
                <div className="error-message">{formErrors.name}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">
                {t("common.description")} <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={
                  t("clubManager.enterDescription") || "Enter club description"
                }
                required
                disabled={submitting}
                rows="4"
                className={formErrors.description ? "input-error" : ""}
                maxLength={500}
              />
              {formErrors.description && (
                <div className="error-message">{formErrors.description}</div>
              )}
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && clubToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setClubToDelete(null);
          }}
          title={t("clubManager.confirmDelete")}
          onConfirm={handleDeleteConfirm}
          confirmText={deleting ? t("common.deleting") : t("common.delete")}
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <h3>{t("clubManager.confirmDelete")}</h3>
            <div className="club-details">
              <p>
                <strong>{t("clubManager.clubName")}:</strong>{" "}
                {clubToDelete.name}
              </p>
              <p>
                <strong>{t("common.description")}:</strong>{" "}
                {clubToDelete.description || "N/A"}
              </p>
              <p>
                <strong>{t("clubManager.createdBy") || "Created By"}:</strong>{" "}
                {clubToDelete.created_by_email}
              </p>
              <p>
                <strong>
                  {t("clubManager.displayOrder") || "Display Order"}:
                </strong>{" "}
                {clubToDelete.display_order || 0}
              </p>
              <p>
                <strong>{t("common.status")}:</strong>
                <span
                  className={`status-badge ${clubToDelete.is_active ? "active" : "inactive"}`}
                >
                  {clubToDelete.is_active
                    ? t("common.active")
                    : t("common.inactive")}
                </span>
              </p>
            </div>
            <p className="warning-text">{t("clubManager.deleteWarning")}</p>
          </div>
        </Modal>
      )}

      <style jsx>{`
        .drag-overlay-row {
          background: white;
          border: 2px solid #007bff;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          opacity: 0.9;
          width: 100%;
          transform: scale(1.02);
        }

        .drag-overlay-row table {
          width: 100%;
          border-collapse: collapse;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

export default ClubManager;
